import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { setUser } from '@/store/slices/userSlice';
import { setInitialized } from '@/store/slices/appSlice';
import { DjangoData } from '@/types';
import { useAuth } from './contexts/AuthContext';

// Page components
import HomePage from '@/pages/HomePage';
import RalliesPage from '@/pages/Rallies';
import RideHub from './pages/RideHub';
import RideDetail from './pages/RideDetail';
import Media from './pages/Media';
import About from './pages/About';
import Contact from './pages/Contact';
import SignInPage from './pages/SignInPage';
import PreLaunchPage from './pages/PreLaunchPage';
import UserProfile from './pages/UserProfile';
import LogBookDetail from './pages/LogBookDetail';
import TicketSuccess from './pages/TicketSuccess';
import SubscriptionSuccess from './pages/SubscriptionSuccess';

// Layout components
import Layout from '@/components/layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { CSRFProvider } from './contexts/CSRFContext';

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>; // You might want to use a proper loading component
  }

  if (!session?.authenticated) {
    return <Navigate to="/coming-soon" replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Initialize app with Django data
    const djangoData = (window as any).__DJANGO_DATA__ as DjangoData;

    if (djangoData) {
      // Set user data from Django
      if (djangoData.user) {
        dispatch(setUser(djangoData.user));
      }

      // Set app configuration if available
      if (djangoData.userProfile) {
        // If we have a user profile, merge it with user data
        dispatch(setUser({ ...djangoData.user, ...djangoData.userProfile }));
      }

      dispatch(setInitialized(true));
    }
  }, [dispatch]);

  return (
    <Routes>
      <Route path="coming-soon" element={<PreLaunchPage />} />
      <Route element={<Layout><Outlet /></Layout>}>
        {/* Public routes */}
        <Route path="sneaky-sign-in" element={<SignInPage />} />
        <Route index element={
            <HomePage />
        } />
        <Route path="rallies" element={
          <RalliesPage />
        } />
        <Route path="explore-rides" element={
          <RideHub />
        } />
        <Route path="explore-rides/:id" element={
          <RideDetail />
        } />
        <Route path="ride-hub" element={
          <RideHub />
        } />
        <Route path="ride-hub/:id" element={
          <RideDetail />
        } />
        <Route path="content-garage" element={
          <Media />
        } />
        <Route path="log-book/:id" element={
          <LogBookDetail />
        } />
        <Route path="about" element={
          <About />
        } />
        <Route path="contact" element={
          <Contact />
        } />
        <Route path="profile/:id" element={
          <UserProfile />
        } />
        <Route path="ticket/success" element={
          <TicketSuccess />
        } />
        <Route path="subscription/success" element={
          <SubscriptionSuccess />
        } />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <CSRFProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AppRoutes />
        </TooltipProvider>
      </AuthProvider>
    </CSRFProvider>
  );
}

export default App; 